export const pick = (obj: any, paths: string[]) => {
  for (const p of paths) {
    const v = p.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

export const extractLeague = (status: any): string => {
  const v =
    pick(status, [
      "c.league",
      "c.leagueName",
      "c.lg",
      "c.league.name",
      "c.tournament.name",
      "c.competition",
      "c.compName",
      "league",
      "leagueName",
      "lg",
      "lgName",
      "competition",
    ]) || "Match";

  if (typeof v === "object" && v) {
    return (v.name as string) || (v.title as string) || "Match";
  }
  return String(v);
};

export const parseDateFlexible = (val: any): Date | null => {
  if (val == null) return null;

  if (typeof val === "number") {
    const ms =
      val >= 1e12
        ? val
        : val >= 1e10
        ? Math.round(val)
        : Math.round(val * 1000);
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof val === "string") {
    const asNum = Number(val);
    if (!Number.isNaN(asNum)) return parseDateFlexible(asNum);
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
};

const parseYYYYMMDDHHmmss = (s: string): Date | null => {
  if (!/^\d{14}$/.test(s)) return null;
  const y = Number(s.slice(0, 4));
  const M = Number(s.slice(4, 6)) - 1;
  const d = Number(s.slice(6, 8));
  const h = Number(s.slice(8, 10));
  const m = Number(s.slice(10, 12));
  const sec = Number(s.slice(12, 14));
  const dt = new Date(y, M, d, h, m, sec);
  return isNaN(dt.getTime()) ? null : dt;
};

export const formatDate24 = (date: Date, withSeconds = false): string => {
  const h = date.getHours().toString().padStart(2, "0");
  const m = date.getMinutes().toString().padStart(2, "0");
  if (withSeconds) {
    const s = date.getSeconds().toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  }
  return `${h}:${m}`;
};

export const extractKickoff = (status: any): Date | null => {
  const raw = pick(status, [
    "ko",
    "kickoff",
    "sd",
    "start",
    "startTime",
    "c.sd",
    "c.kickoff",
    "c.start",
    "c.startTime",
    "c.ko",
    "c.matchStart",
  ]);
  const direct = parseDateFlexible(raw);
  if (direct) return direct;

  const stsRaw: unknown =
    pick(status, ["sts", "c.sts"]) ??
    (typeof status?.stn === "string" ? status.stn : undefined);

  if (typeof stsRaw === "string") {
    const ts = stsRaw.match(/(^|[|])(\d{14})=:/);
    if (ts && ts[2]) {
      const fromSts = parseYYYYMMDDHHmmss(ts[2]);
      if (fromSts) return fromSts;
    }
  }

  return null;
};

export const toFormState = (v: any): number => {
  if (v === 1 || v === "W" || v === "w") return 1;
  if (v === 0 || v === "D" || v === "d") return 0;
  if (v === -1 || v === "L" || v === "l") return -1;
  return 0;
};

export type FormPoint = { state: number; venue?: "H" | "A" | "N" };

export type RecentMatch = {
  opponent: string;
  result?: string | null;
  location?: string | null;
};

export type MatchDetails = {
  league?: string | null;
  stadium?: string | null;
  weather?: string | null;
  tickets?: string | null;
  additionalInfo?: string | null;
  officials: string[];
};

export const parseFormFromSts = (status: any): [FormPoint[], FormPoint[]] => {
  const stsRaw: unknown =
    pick(status, ["sts", "c.sts"]) ??
    (typeof status?.stn === "string" ? status.stn : undefined);

  if (typeof stsRaw !== "string") return [[], []];

  const m = stsRaw.match(/(?:^|\|)INFO=([^|]+)/);
  if (!m || !m[1]) return [[], []];

  const [homeStr = "", awayStr = ""] = m[1].split("~");

  const parseSide = (side: string): FormPoint[] => {
    if (!side) return [];
    const tokens = side.split("#").filter(Boolean);
    const mapRec = (rec: string): FormPoint => {
      const parts = rec.split(",").map((s) => s.trim());
      const letter = (parts[0] || "").charAt(0).toUpperCase();
      const venue = (parts[1] || "").charAt(0).toUpperCase() as "H" | "A" | "N";
      let state = 0;
      if (letter === "W") state = 1;
      else if (letter === "L") state = -1;
      else state = 0;
      return {
        state,
        venue:
          venue === "H" || venue === "A" || venue === "N" ? venue : undefined,
      };
    };
    return tokens.slice(0, 5).map(mapRec);
  };

  return [parseSide(homeStr), parseSide(awayStr)];
};

const sanitizeText = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => sanitizeText(item))
      .filter((item): item is string => Boolean(item));
    return parts.length ? parts.join(", ") : null;
  }
  if (typeof value === "object") {
    const parts = Object.values(value as Record<string, unknown>)
      .map((item) => sanitizeText(item))
      .filter((item): item is string => Boolean(item));
    return parts.length ? parts.join(", ") : null;
  }
  return null;
};

const normalizeOfficials = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeText(item))
      .filter((item): item is string => Boolean(item));
  }
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>)
      .map((item) => sanitizeText(item))
      .filter((item): item is string => Boolean(item));
  }
  if (typeof value === "string") {
    return value
      .split(/[;,]|(?:\s{2,})/)
      .map((p) => p.trim())
      .filter(Boolean);
  }
  return [];
};

const toTicketsText = (value: unknown): string | null => {
  if (value == null) return null;
  const numValue = Number(value);
  if (!Number.isNaN(numValue) && Number.isFinite(numValue)) {
    return Math.round(numValue).toLocaleString();
  }
  return sanitizeText(value);
};

export const extractMatchDetails = (status: any): MatchDetails => {
  const leagueName = sanitizeText(extractLeague(status));
  const stadium = sanitizeText(
    pick(status, [
      "c.matchInfo.stadium",
      "c.stadium",
      "c.venue.name",
      "c.venue",
      "stadium",
      "venue",
      "c.ground",
    ])
  );
  const weather = sanitizeText(
    pick(status, [
      "c.matchInfo.weather",
      "c.weather",
      "weather",
      "c.conditions",
      "conditions",
    ])
  );
  const tickets = toTicketsText(
    pick(status, [
      "c.matchInfo.tickets",
      "c.ticketsSold",
      "c.tickets",
      "tickets",
      "ticketsSold",
      "c.attendance",
      "attendance",
    ])
  );
  const additionalInfo = sanitizeText(
    pick(status, [
      "c.matchInfo.additionalInfo",
      "c.additionalInfo",
      "c.notes",
      "additionalInfo",
      "notes",
    ])
  );
  const officialRaw =
    pick(status, [
      "c.matchInfo.officials",
      "c.officials",
      "officials",
      "c.referees",
      "referees",
      "c.matchOfficials",
    ]) ?? [];

  return {
    league: leagueName,
    stadium,
    weather,
    tickets,
    additionalInfo,
    officials: normalizeOfficials(officialRaw),
  };
};
